import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import AskAISection from './AskAISection';
import { loadLocalPagesPublicSettings } from '@/lib/localPagesPublic';

export default function Footer() {
  const [hoveredSocial, setHoveredSocial] = useState<string | null>(null);
  const [imgErrors, setImgErrors] = useState<Record<string, boolean>>({});
  // "Service areas" is the ONLY main-site link into the Local Service Pages
  // (never the home page or the main nav). It can be turned off in Settings.
  const [showServiceAreas, setShowServiceAreas] = useState(true);

  useEffect(() => {
    let cancelled = false;
    loadLocalPagesPublicSettings().then((s) => { if (!cancelled) setShowServiceAreas(s.footerLinkEnabled); });
    return () => { cancelled = true; };
  }, []);

  const partners = [
    { name: 'Infor Partner', logo: 'https://storage.readdy-site.link/project_files/e75b0322-97ac-47e3-9448-b32eefec9d7f/9951b7f9-af8e-415f-9a59-bd87b6b71ad4_infor-logo.png?v=0f08be5611ace49516b5d5c4496d3f37', link: '/partnerships/infor' },
    { name: 'Microsoft Partner', logo: 'https://storage.readdy-site.link/project_files/e75b0322-97ac-47e3-9448-b32eefec9d7f/1ddcd66c-3406-4111-ad6e-068723be2295_Microsoft-Logo-PNG-transparent_1.png?v=0322b548272c056b0daf2ed26bd04be7', link: '/partnerships/microsoft' },
    { name: 'AWS Partner', logo: 'https://storage.readdy-site.link/project_files/e75b0322-97ac-47e3-9448-b32eefec9d7f/553a84d2-e03a-44a4-a009-4033465b551b_AWS_Simple_Icons_AWS_Cloud.svg.png?v=c5114358470bf2d5f2c3d712615d6772', link: '/partnerships/aws' },
    { name: 'Oracle Partner', logo: '', link: '/partnerships/oracle' },
    { name: 'ConnectWise', logo: 'https://storage.readdy-site.link/project_files/e75b0322-97ac-47e3-9448-b32eefec9d7f/5e4176b2-c053-409c-af27-bb3197113c14_connectwise-cw-vert-master.png?v=d64df49dc95f6f40b7ea3eaa41784584', link: '/partnerships/connectwise' },
  ];

  const socialLinks = [
    { name: 'LinkedIn', icon: 'ri-linkedin-fill', url: 'https://linkedin.com/company/cendien' },
    { name: 'Instagram', icon: 'ri-instagram-line', url: 'https://instagram.com/cendien' },
    { name: 'Facebook', icon: 'ri-facebook-fill', url: 'https://facebook.com/cendien' },
    { name: 'YouTube', icon: 'ri-youtube-fill', url: 'https://youtube.com/@cendien' },
  ];

  return (
    <footer className="bg-slate-900 text-white">
      {/* Cendien Difference Highlight Strip */}
      <div className="bg-teal-700 border-b border-teal-600">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8 w-full md:w-auto">
              <div className="flex items-center gap-3">
                <img src="/images/icons/related/request-consultant.webp" alt="" width={56} height={56} loading="lazy" decoding="async" className="w-14 h-14 rounded-xl flex-shrink-0 shadow-md" />
                <div>
                  <h3 className="font-bold text-lg text-white">The Cendien Difference</h3>
                  <p className="text-teal-50 text-sm">Request one expert. Get the whole Cendien team.</p>
                </div>
              </div>
              <div className="flex flex-col lg:flex-row items-center gap-3 lg:gap-6 w-full md:w-auto">
                <div className="flex items-center gap-2 text-teal-50 w-full md:w-auto justify-center md:justify-start">
                  <img src="/images/icons/related/team-backing.webp" alt="" width={44} height={44} loading="lazy" decoding="async" className="w-11 h-11 rounded-lg flex-shrink-0 shadow-sm" />
                  <span className="text-sm font-medium whitespace-nowrap">Full Team Backing</span>
                </div>
                <div className="flex items-center gap-2 text-teal-50 w-full md:w-auto justify-center md:justify-start">
                  <img src="/images/icons/related/decades-knowledge.webp" alt="" width={44} height={44} loading="lazy" decoding="async" className="w-11 h-11 rounded-lg flex-shrink-0 shadow-sm" />
                  <span className="text-sm font-medium whitespace-nowrap">27 Years Knowledge</span>
                </div>
                <div className="flex items-center gap-2 text-teal-50 w-full md:w-auto justify-center md:justify-start">
                  <img src="/images/icons/related/dedicated-support.webp" alt="" width={44} height={44} loading="lazy" decoding="async" className="w-11 h-11 rounded-lg flex-shrink-0 shadow-sm" />
                  <span className="text-sm font-medium whitespace-nowrap">Dedicated Support</span>
                </div>
              </div>
            </div>
            <a
              id="footer-hire-cta"
              href="/request-consultants#request-form"
              data-hire-cta="footer-strip"
              className="bg-white text-teal-700 px-6 py-2.5 rounded-lg hover:bg-teal-50 transition-colors text-sm font-semibold whitespace-nowrap inline-flex items-center gap-2"
            >
              Request Consultants
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer Grid */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 mb-12">

          {/* Left Column: Company Info + Contact */}
          <div className="lg:col-span-2 flex flex-col gap-8">
            {/* Logo + description + social */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img
                  src="https://static.readdy.ai/image/8e0505aa0635a0a18473d6b2a0ce03ab/323af627e9bf3eb9436b19c25ae124a5.png"
                  alt="Cendien Logo"
                  className="h-14 w-auto object-contain"
                />
              </div>
              <p className="text-gray-400 mb-6 leading-relaxed text-sm">
                Enterprise IT consulting and managed services. Request one consultant, get us all — backed by 27 years of expertise, technical leadership, and a team that works exceptionally well together.
              </p>
              <div className="flex items-center gap-3 relative">
                {socialLinks.map((social) => (
                  <div key={social.name} className="relative">
                    <a
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-teal-500 hover:text-white transition-all duration-300 hover:scale-105"
                      aria-label={social.name}
                      onMouseEnter={() => setHoveredSocial(social.name)}
                      onMouseLeave={() => setHoveredSocial(null)}
                    >
                      <i className={social.icon}></i>
                    </a>
                    {hoveredSocial === social.name && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                        {social.name}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                          <div className="border-4 border-transparent border-t-slate-800"></div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Get in Touch */}
            <div>
              <h4 className="font-bold mb-4 text-sm uppercase tracking-[0.2em] text-white">Get in Touch</h4>
              <div className="space-y-2 text-sm text-white/60">
                <div className="flex items-start gap-2">
                  <div className="w-8 h-8 rounded-md bg-white/10 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-map-pin-line text-sm"></i>
                  </div>
                  <span className="text-xs">1846 E. Rosemeade Pkwy, Suite 200<br />Carrollton, TX 75007</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-md bg-white/10 flex items-center justify-center shrink-0">
                    <i className="ri-phone-line text-sm"></i>
                  </div>
                  <a href="tel:+12142454580" className="text-xs hover:text-white transition-colors relative inline-block group">
                    (214) 245-4580
                    <span className="absolute bottom-0 left-0 w-0 h-px bg-white transition-all duration-200 group-hover:w-full"></span>
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-md bg-white/10 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-sm"></i>
                  </div>
                  <a href="mailto:info@cendien.com" className="text-xs hover:text-white transition-colors relative inline-block group">
                    info@cendien.com
                    <span className="absolute bottom-0 left-0 w-0 h-px bg-white transition-all duration-200 group-hover:w-full"></span>
                  </a>
                </div>
              </div>
              <Link
                to="/request-consultants#request-form"
                data-hire-cta="footer-contact"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 h-9 px-4 rounded-lg bg-teal-500 hover:bg-teal-600 text-white text-xs font-semibold transition-all duration-300 hover:shadow-lg whitespace-nowrap"
              >
                Request Consultants
              </Link>
            </div>
          </div>

          {/* Right Columns: Nav + Ask AI */}
          <div className="lg:col-span-3 flex flex-col gap-8">
            {/* Nav columns row */}
            <div className="grid grid-cols-3 gap-8">
              <div>
                <h4 className="font-bold mb-4 text-sm uppercase tracking-[0.2em] text-white">Company</h4>
                <ul className="space-y-2 text-sm text-white/60">
                  {[
                    { label: 'About', to: '/about' },
                    { label: 'Engagement Models', to: '/engagement-models' },
                    { label: 'Resources', to: '/resources' },
                    { label: 'Case Studies', to: '/case-studies' },
                    { label: 'Insights', to: '/insights' },
                    { label: 'Newsroom', to: '/newsroom' },
                    { label: 'Videos', to: '/videos' },
                    { label: 'Cendien Originals', to: '/originals' },
                    { label: 'Training Videos', to: '/videos/training' },
                    { label: 'Academy', to: '/academy' },
                    { label: 'Partners', to: '/partnerships' },
                    { label: 'Careers', to: '/careers' },
                    { label: 'Contact', to: '/contact' },
                  ].map((item) => (
                    <li key={item.to}>
                      <Link to={item.to} className="hover:text-white transition-colors relative inline-block group">
                        {item.label}
                        <span className="absolute bottom-0 left-0 w-0 h-px bg-white transition-all duration-200 group-hover:w-full"></span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-bold mb-4 text-sm uppercase tracking-[0.2em] text-white">Services</h4>
                <ul className="space-y-2 text-sm text-white/60">
                  {[
                    { label: 'Managed IT Services', to: '/services/managed' },
                    { label: 'Managed IT Pricing Calculator', to: '/services/managed/pricing' },
                    { label: 'Managed IT Packages', to: '/services/managed/packages' },
                    { label: 'ERP Solutions', to: '/services/erp' },
                    { label: 'Infor Support', to: '/services/erp/infor-support' },
                    { label: 'Infor Lawson Support', to: '/services/erp/infor-lawson' },
                    { label: 'Infor CloudSuite Support', to: '/services/erp/infor-cloudsuite' },
                    { label: 'Workday Support', to: '/services/erp/workday-support' },
                    { label: 'Oracle Support', to: '/services/erp/oracle-support' },
                    { label: 'PeopleSoft Support', to: '/services/erp/peoplesoft-support' },
                    { label: 'IT Staffing', to: '/services/staffing' },
                    { label: 'AI Solutions', to: '/services/ai' },
                    { label: 'Cloud Services', to: '/services/cloud' },
                    { label: 'Service Desk', to: '/services/service-desk' },
                    { label: 'Healthcare IT', to: '/services/healthcare-it' },
                    { label: 'Strategy Advisory', to: '/services/strategy-advisory' },
                  ].map((item) => (
                    <li key={item.to}>
                      <Link to={item.to} className="hover:text-white transition-colors relative inline-block group">
                        {item.label}
                        <span className="absolute bottom-0 left-0 w-0 h-px bg-white transition-all duration-200 group-hover:w-full"></span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-bold mb-4 text-sm uppercase tracking-[0.2em] text-white">Industries</h4>
                <ul className="space-y-2 text-sm text-white/60">
                  {[
                    { label: 'Healthcare', to: '/industries/healthcare' },
                    { label: 'Financial Services', to: '/industries/financial-services' },
                    { label: 'Government', to: '/industries/government' },
                    { label: 'Government Contracting', to: '/government-contracting' },
                    { label: 'Manufacturing', to: '/industries/manufacturing' },
                    { label: 'Retail', to: '/industries/retail' },
                    { label: 'Education', to: '/industries/education' },
                    { label: 'Areas we serve', to: '/about/dallas-coverage' },
                    ...(showServiceAreas ? [{ label: 'Service areas', to: '/service-areas' }] : []),
                    { label: 'View All', to: '/industries' },
                  ].map((item) => (
                    <li key={item.to}>
                      <Link to={item.to} className="hover:text-white transition-colors relative inline-block group">
                        {item.label}
                        <span className="absolute bottom-0 left-0 w-0 h-px bg-white transition-all duration-200 group-hover:w-full"></span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Ask AI About Cendien — GovDash-style box */}
            <div className="rounded-lg border border-white/10 bg-white/5 px-4 py-3 footer-ai-glow">
              <AskAISection variant="dark" compact mini />
            </div>
          </div>
        </div>

        {/* Partner Logos Strip */}
        <div className="border-t border-white/10 pt-8 pb-8">
          <p className="text-xs font-bold uppercase tracking-widest text-white/40 text-center mb-5">Trusted Technology Partners</p>
          <div className="flex items-center justify-center gap-8 flex-wrap">
            {partners.map((partner) => (
              <Link
                key={partner.name}
                to={partner.link}
                className="group flex items-center justify-center"
                title={partner.name}
              >
                {partner.logo && !imgErrors[partner.name] ? (
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className="h-8 w-auto object-contain opacity-40 grayscale group-hover:opacity-100 group-hover:grayscale-0 transition-all duration-300"
                    onError={() => setImgErrors((prev) => ({ ...prev, [partner.name]: true }))}
                  />
                ) : (
                  <span className="text-xs font-semibold text-white/40 group-hover:text-white/80 transition-colors tracking-wide uppercase px-3 py-1.5 border border-white/10 rounded-md group-hover:border-white/30">
                    {partner.name}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-6 text-xs text-white/50">
            <span>© 2026 Cendien. All rights reserved.</span>
            <span className="hidden md:inline text-white/20">|</span>
            <span>Carrollton, TX · Since 1999</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/privacy-policy" className="text-xs text-white/50 hover:text-white/80 transition-colors relative inline-block group">
              Privacy Policy
              <span className="absolute bottom-0 left-0 w-0 h-px bg-white/80 transition-all duration-200 group-hover:w-full"></span>
            </Link>
            <Link to="/terms-of-service" className="text-xs text-white/50 hover:text-white/80 transition-colors relative inline-block group">
              Terms of Service
              <span className="absolute bottom-0 left-0 w-0 h-px bg-white/80 transition-all duration-200 group-hover:w-full"></span>
            </Link>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-teal-500 hover:text-white hover:-translate-y-1 transition-all duration-300 cursor-pointer"
              aria-label="Back to top"
            >
              <i className="ri-arrow-up-line"></i>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}

export { Footer };